import React, { useState, useEffect, useCallback } from "react";
import { Order, LeadComment } from "../../types";
import { API_BASE_URL } from "../../utils/apiConfig";
import {
  X,
  Phone,
  Mail,
  MapPin,
  Package,
  DollarSign,
  Calendar,
  MessageSquare,
  RefreshCw,
  User,
} from "lucide-react";
import { formatPhoneWithCountryCode } from "../../utils/phoneFormatter";
import { formatTimestampFull } from "../../utils/dateFormatter";
import { getCountryFlag } from "../Shipping/utils";
import { getStatusDisplayName } from "../../utils/statusMapper";

interface LeadDetailsViewProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  currentUser: {
    id: string;
    username: string;
    role: string;
  };
}

const LeadDetailsView: React.FC<LeadDetailsViewProps> = ({
  isOpen,
  onClose,
  order,
  currentUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<LeadComment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [savingComment, setSavingComment] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!order || !order.phone) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      
      // Extract first and last name from order.name
      const nameParts = (order.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Fetch comments
      const commentsResponse = await fetch(
        `${API_BASE_URL}/api/enhanced-orders/leads/${encodeURIComponent(order.phone)}/comments?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`,
        {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json();
        if (commentsData.success) {
          setComments(commentsData.data.comments || []);
        }
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  }, [order]);

  useEffect(() => {
    if (isOpen && order) {
      fetchComments();
    }
  }, [isOpen, order, fetchComments]);

  const handleAddComment = async () => {
    if (!order || !newComment.trim() || !order.phone) return;

    setSavingComment(true);
    try {
      const token = localStorage.getItem("authToken");
      
      // Extract first and last name
      const nameParts = (order.name || '').trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await fetch(
        `${API_BASE_URL}/api/enhanced-orders/leads/${encodeURIComponent(order.phone)}/comments`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            comment: newComment.trim(),
            firstName,
            lastName,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setNewComment("");
          fetchComments(); // Refresh comments
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setSavingComment(false);
    }
  };

  if (!isOpen || !order) return null;

  // Extract first and last name
  const nameParts = (order.name || '').trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Lead Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              {order.name} • {formatPhoneWithCountryCode(order.phone, order.country)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Lead Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <User className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-sm text-gray-500">Name</div>
                    <div className="text-sm font-medium text-gray-900">{order.name || "N/A"}</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <div className="text-sm font-medium font-mono text-gray-900">
                      {formatPhoneWithCountryCode(order.phone, order.country)}
                    </div>
                  </div>
                </div>
                {order.email && (
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="text-sm font-medium text-gray-900">{order.email}</div>
                    </div>
                  </div>
                )}
                {order.country && (
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Location</div>
                      <div className="text-sm font-medium text-gray-900">
                        {getCountryFlag(order.country)} {order.country.toUpperCase()}
                        {order.city && `, ${order.city}`}
                      </div>
                    </div>
                  </div>
                )}
                {order.leadOwner && (
                  <div className="flex items-center">
                    <User className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Assigned To</div>
                      <div className="text-sm font-medium text-gray-900">
                        {order.leadOwner.username} ({order.leadOwner.role})
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Order</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Order ID:</span>
                  <span className="text-sm font-medium text-gray-900">#{order.id}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Product:</span>
                  <span className="text-sm font-medium text-gray-900">{order.offername || "N/A"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Quantity:</span>
                  <span className="text-sm font-medium text-gray-900">{order.count || 1}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Price:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {order.price || 0} {order.currency || ""}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {getStatusDisplayName(order)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Created:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatTimestampFull(order.time)}
                  </span>
                </div>
              </div>
            </div>

            {/* Comments Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2" />
                Comments
              </h3>
              <div className="space-y-4">
                {/* Add Comment */}
                <div className="flex gap-2">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment about this lead..."
                    rows={3}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || savingComment}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingComment ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      "Add"
                    )}
                  </button>
                </div>

                {/* Comments List */}
                <div className="space-y-2">
                  {loading ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                      <span className="ml-2 text-gray-600">Loading comments...</span>
                    </div>
                  ) : (
                    <>
                      {comments.map((comment, idx) => (
                        <div
                          key={idx}
                          className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-gray-900">{comment.content}</p>
                              <div className="mt-2 text-xs text-gray-500">
                                {comment.editedBy ? (
                                  <span>
                                    {comment.editedBy.username} ({comment.editedBy.role}) •{" "}
                                    {new Date(comment.timestamp).toLocaleString()}
                                  </span>
                                ) : (
                                  <span>
                                    {new Date(comment.timestamp).toLocaleString()}
                                  </span>
                                )}
                                {comment.orderId && (
                                  <span> • Order #{comment.orderId}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {comments.length === 0 && (
                        <p className="text-gray-500 text-center py-4">No comments yet</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailsView;

